import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Badge from '@shared/components/Badge';
import { useAlarmSummary } from '@/lib/useAlarmSummary';
import SidebarUserPanel from './SidebarUserPanel';
import RegionChip from './RegionChip';
import { Icon, type IconName } from '@shared';

interface NavItem {
  label: string;
  icon: IconName;
  path: string;
  /** Contador a la derecha. `alarms`: abiertas + reconocidas (las silenciadas
   *  no cuentan), rojo si la peor es error o crítica. */
  badge?: 'alarms';
}

interface NavGroup {
  /** Clave estable: el colapso se persiste por id, no por etiqueta, para
   *  poder renombrar un grupo sin perder lo que cada uno tenía plegado. */
  id: string;
  // `null` = pinned items rendered without a group header (Dashboard).
  label: string | null;
  items: NavItem[];
}

// Grupos por lo que hace el operador, no por dónde vive el dato:
// «a quién atiendo», «qué cobro», «qué ofrecemos», «cómo funciona la máquina»
// y «nuestro trabajo». Reorganizado el 2026-10-25 (decisión del dueño):
// Estadísticas sube junto al Dashboard (es para leer), Configuración entra en
// Sistema (antes sólo estaba en el menú de perfil) y las versiones del agente
// tienen página propia en Sistema, porque mover el canal prod es un mando de
// producción. Los ids de grupo no cambian: con ellos se guarda lo plegado.
const NAV_GROUPS: NavGroup[] = [
  {
    id: 'pinned',
    label: null,
    items: [
      { label: 'Dashboard', icon: 'dashboard', path: '/' },
      { label: 'Estadísticas', icon: 'analytics', path: '/analytics' },
      { label: 'Monitor', icon: 'monitor_heart', path: '/monitor', badge: 'alarms' },
    ],
  },
  {
    id: 'talleres',
    label: 'Talleres y personas',
    items: [
      { label: 'Talleres', icon: 'business', path: '/talleres' },
      { label: 'Usuarios', icon: 'group', path: '/usuarios' },
      { label: 'Soporte', icon: 'support_agent', path: '/soporte' },
      { label: 'Migraciones', icon: 'drive_file_move', path: '/migraciones' },
    ],
  },
  {
    id: 'ingresos',
    label: 'Ingresos',
    items: [
      { label: 'Suscripciones', icon: 'credit_card', path: '/suscripciones' },
      { label: 'Planes', icon: 'sell', path: '/planes' },
      { label: 'Financiero', icon: 'account_balance', path: '/financiero' },
      { label: 'Partners', icon: 'handshake', path: '/partners' },
    ],
  },
  {
    // Fue «Plataforma»: lo que define cómo es BolloPrint para el taller.
    id: 'plataforma',
    label: 'Producto',
    items: [
      { label: 'Países', icon: 'public', path: '/paises' },
      { label: 'Catálogo', icon: 'menu_book', path: '/catalogo' },
      { label: 'Email', icon: 'mail', path: '/email' },
      { label: 'Traducciones', icon: 'translate', path: '/traducciones' },
    ],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    items: [
      { label: 'Impresoras', icon: 'print', path: '/impresoras' },
      { label: 'Versiones del agente', icon: 'system_update_alt', path: '/versiones-agente' },
      { label: 'Procesos', icon: 'memory', path: '/procesos' },
      { label: 'Almacenamiento', icon: 'cloud', path: '/almacenamiento' },
      { label: 'Configuración', icon: 'settings', path: '/configuracion' },
    ],
  },
  {
    id: 'desarrollo',
    label: 'Desarrollo',
    items: [
      { label: 'Tareas y roadmap', icon: 'code', path: '/desarrollo' },
      { label: 'Test', icon: 'science', path: '/test' },
    ],
  },
];

const COLLAPSED_STORAGE_KEY = 'admin.sidebar.collapsedGroups';
// Lo guardado con la organización anterior (por etiqueta) se lee una vez y
// se traduce al id del grupo que hereda esa sección.
const LEGACY_LABEL_TO_ID: Record<string, string> = { 'Operación': 'talleres', 'Ventas': 'ingresos', 'Soporte': 'plataforma', 'Sistema': 'sistema' };

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `group flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
    isActive
      ? 'text-primary-dark dark:text-primary bg-primary/5 dark:bg-primary/10'
      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-surface-hover-dark dark:hover:text-white'
  }`;

const activeIndicator = (isActive: boolean) =>
  isActive ? <span className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-sm" /> : null;

interface Props {
  onClose?: () => void;
}

const Sidebar: React.FC<Props> = ({ onClose }) => {
  const location = useLocation();
  // Se monta dos veces (escritorio y cajón de móvil); el almacén pregunta una.
  const { summary: alarmSummary } = useAlarmSummary();
  const attention = alarmSummary?.attention ?? 0;

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(COLLAPSED_STORAGE_KEY) || '{}') as Record<string, boolean>;
      const out: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(raw)) out[LEGACY_LABEL_TO_ID[k] ?? k] = v;
      return out;
    } catch {
      return {};
    }
  });

  const toggleGroup = (id: string) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // A group that holds the active route always shows its items, even if the
  // user previously collapsed it — never hide the page you're on.
  const groupHasActive = (group: NavGroup) =>
    group.items.some((it) => (it.path === '/' ? location.pathname === '/' : location.pathname.startsWith(it.path)));

  const renderNavItem = (item: NavItem) => (
    <NavLink key={item.path} to={item.path} end={item.path === '/'} onClick={onClose} className={navLinkClass}>
      {({ isActive }) => (
        <>
          {activeIndicator(isActive)}
          <Icon name={item.icon} className={`!text-[20px] ${
              isActive ? 'text-primary' : 'group-hover:text-slate-600 dark:group-hover:text-slate-300'
            }`} />
          <span>{item.label}</span>
          {item.badge === 'alarms' && attention > 0 ? (
            <span className="ml-auto" title={`${attention} ${attention === 1 ? 'alarma requiere' : 'alarmas requieren'} atención`}>
              <Badge tone={alarmSummary?.worst === 'critical' || alarmSummary?.worst === 'error' ? 'danger' : 'warning'}>
                {attention > 99 ? '99+' : attention}
              </Badge>
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );

  return (
    <aside className="w-64 h-full flex-shrink-0 border-r border-border-subtle dark:border-border-subtle-dark bg-white dark:bg-surface-dark flex flex-col">
      {/* Brand — top block. RegionChip lives outside the <Link> so dropdown
          clicks (multi-region) don't navigate home. `h-topbar` matches the
          `PageShell` header so their bottom borders are one line. */}
      <div className="flex items-center gap-3 px-4 h-topbar border-b border-border-subtle dark:border-border-subtle-dark shrink-0">
        <Link
          to="/"
          onClick={onClose}
          className="flex items-center gap-3 min-w-0 flex-1 hover:opacity-80 transition-opacity"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg overflow-hidden shrink-0">
            <img src="/logo.png" alt="BolloPrint" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-sm font-bold leading-tight tracking-tight truncate">
            <span className="text-slate-800 dark:text-white">BolloPrint</span>
            <span className="text-primary ml-1">Admin</span>
          </h1>
        </Link>
        <RegionChip />
      </div>

      {/* Navigation — middle block (flexes to fill) */}
      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
        <nav className="flex flex-col gap-1 p-4">
          {NAV_GROUPS.map((group) => {
            if (!group.label) {
              return <React.Fragment key={group.id}>{group.items.map(renderNavItem)}</React.Fragment>;
            }
            const collapsed = !!collapsedGroups[group.id] && !groupHasActive(group);
            return (
              <div key={group.id} className="flex flex-col gap-1 mt-3 first:mt-0">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={!collapsed}
                  aria-controls={`nav-group-${group.id}`}
                  className="group/head flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-text-sub dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  <Icon name="expand_more" className={`!text-[16px] transition-transform ${collapsed ? '-rotate-90' : ''}`} aria-hidden="true" />
                  <span>{group.label}</span>
                </button>
                <div id={`nav-group-${group.id}`} className="flex flex-col gap-1">
                  {!collapsed && group.items.map(renderNavItem)}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Bottom group: toast slot stacks growing upward, right above the
            Registro link. ToastProvider (shared) portals into #bp-toast-slot
            when present. empty:hidden keeps it from adding spacing when there
            are no toasts. The last-events feed that lived here moved to
            Monitor → Registro, which can filter and page what a list of eight
            could not. */}
        <div className="mt-auto">
          <div id="bp-toast-slot" className="flex flex-col gap-2 px-3 pb-2 empty:hidden" />
          <div className="px-4 py-3">
            <Link
              to="/monitor?tab=registro"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-text-sub dark:text-slate-400 hover:bg-slate-50 hover:text-slate-800 dark:hover:bg-surface-hover-dark dark:hover:text-white transition-colors"
            >
              <Icon name="history" className="!text-[18px]" aria-hidden="true" />
              Ver registro
            </Link>
          </div>
        </div>
      </div>

      {/* User + theme — bottom block */}
      <SidebarUserPanel />
    </aside>
  );
};

export default Sidebar;
